import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Send, ArrowLeft, Users, Loader2, Check, Quote } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareStoryPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [story, setStory] = useState("");
  const [relType, setRelType] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stories`).then(r => setStories(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!story.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/stories/submit`, { name: name.trim() || "Anonymous", story: story.trim(), relationship_type: relType || null });
      setSubmitted(true);
      setName("");
      setStory("");
      setRelType("");
      toast.success("Thank you for sharing your story.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const typeColors = {
    romantic: "bg-[#FFB7B2]/15 text-[#FFB7B2]",
    family: "bg-[#A69BE0]/15 text-[#A69BE0]",
    workplace: "bg-[#4FB3D4]/15 text-[#4FB3D4]",
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="share-story-section">
        <div className="container mx-auto max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-14 h-14 rounded-2xl bg-[#FFB7B2]/15 flex items-center justify-center mx-auto mb-4">
              <Users className="h-7 w-7 text-[#FFB7B2]" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="share-story-title">
              Share Your Story
            </h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">
              A cord of three strands is not easily broken. Every story shared makes our community stronger. Your courage helps others realize they are not alone.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-10">
            {/* Submission Form */}
            <div className="lg:col-span-2">
              <Card className="rounded-2xl shadow-card border border-[#E5E7EB]/50 sticky top-24" data-testid="story-form-card">
                <CardContent className="p-6">
                  {submitted ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                        <Check className="h-7 w-7 text-green-500" />
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-2">Thank You</h3>
                      <p className="text-sm text-[#1A2A3A]/60 mb-4">Your story has been submitted and will be reviewed. Your courage helps others.</p>
                      <Button onClick={() => setSubmitted(false)} variant="outline" className="rounded-full px-5 py-2 text-sm" data-testid="submit-another-btn">
                        Share Another Story
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-1">Your Story Matters</h3>
                      <p className="text-xs text-[#1A2A3A]/50 mb-3">All submissions are reviewed before publishing. You can stay anonymous.</p>
                      <div>
                        <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Name (optional)</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Anonymous" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="story-name-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Relationship Type</label>
                        <select value={relType} onChange={e => setRelType(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="story-type-select">
                          <option value="">Select (optional)</option>
                          <option value="romantic">Romantic Partner</option>
                          <option value="family">Family Member</option>
                          <option value="workplace">Workplace</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Your Story *</label>
                        <textarea value={story} onChange={e => setStory(e.target.value)} rows={6} required placeholder="Share as much or as little as you are comfortable with. Your words may help someone who is in the situation you were in..." className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm resize-none" data-testid="story-text-input" />
                      </div>
                      <Button type="submit" disabled={!story.trim() || submitting} className="w-full bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full py-4 font-semibold text-sm" data-testid="story-submit-btn">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        {submitting ? "Submitting..." : "Share Your Story"}
                      </Button>
                      <p className="text-[10px] text-[#1A2A3A]/40 text-center">We do not share personal information. Stories are reviewed for safety before publishing.</p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Community Wall */}
            <div className="lg:col-span-3">
              <h2 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-5 flex items-center gap-2">
                <Heart className="h-5 w-5 text-[#FFB7B2]" /> Community Wall
              </h2>
              {loading ? (
                <div className="space-y-4">
                  {[1,2,3].map(i => <div key={i} className="animate-pulse h-28 bg-white rounded-xl" />)}
                </div>
              ) : (
                <div className="space-y-4">
                  {stories.map((s, i) => (
                    <Card key={i} className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50 overflow-hidden" data-testid={`story-card-${i}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <Quote className="h-5 w-5 text-[#DCD6F7] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-[#1A2A3A]/70 leading-relaxed italic mb-3">"{s.story}"</p>
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-semibold text-[#1A2A3A]">— {s.name || "Anonymous"}</span>
                              {s.relationship_type && (
                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${typeColors[s.relationship_type] || "bg-gray-100 text-gray-500"}`}>
                                  {s.relationship_type}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-heading text-lg font-bold">ThryvLife</div>
          <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB). All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ShareStoryPage;
